import React from 'react';
import { render } from 'react-dom';
import throttle from 'lodash/throttle';
import { activateScripts } from '@zalando-internal/zalando-consent-api';
import { subscribeToConsentChange as subscribeToSentryConsentChange } from '@pegasus/sentry';
import { WebVitalsCollector, CMAGCollector, FEATURE_KEY } from '@pegasus/web-vitals';
import { loadConfig, UrlSource } from '@browsing/config';
import 'what-input';
import 'intersection-observer';
import 'whatwg-fetch';

import { isSentryEnabled } from '@shared/sentry';
import { getTranslations } from '../locales/translations';
import replaceWith from './helpers/replaceWith';
import { setClientDimensions, setClientOffset } from './helpers/clientDimensions';
import { getVisibilityChangeEventName, setTabVisibility } from './helpers/tabVisibility';
import { isIE } from './helpers/browser';
import { addClassListToSVG } from './helpers/shims';

// just require it so it gets copied on deploy by webpack
require('../favicon.ico');

import makeRoutes from './routes';
import configureStore from './redux/configureStore';
import AppRoot from './containers/AppRoot';

const config = loadConfig();
const locale = config.localeCode;

/* eslint-disable no-console */
// add third-party consents scripts based on consent
activateScripts()
    .then(
        () => console.log('Third party scripts with consent are activated'),
        () => console.error('An error has occurred activatiing scripts'),
    )
    .finally(() => {
        // subscribe to sentry consent changes
        if (isSentryEnabled(config)) {
            subscribeToSentryConsentChange();
        }
    });

WebVitalsCollector(CMAGCollector({ featureKey: FEATURE_KEY.mylounge, flowId: window?.flowId }));

let initialState = {};

// Try to re-use initial state from server side rendering on client
// as window is undefined on server, we need to wrap this into try/catch
try {
    if (window.__INITIAL_STATE__) {
        initialState = { ...initialState, ...window.__INITIAL_STATE__ };
        delete initialState.date;
        delete initialState.processingTime;
        delete window.__INITIAL_STATE__;
    }
} catch (e) {
    console.error('catch', e);
}

// SVG classList shim for IE
if (isIE()) {
    addClassListToSVG();
}

// This is to make webpack async load from the correct place
if (!__DEV__) {
    // eslint-disable-next-line
    __webpack_public_path__ = config.getUrl(UrlSource.ASSETS);
}

// Create redux store.
const store = configureStore(initialState);

// Now that we have the Redux store, we can create our routes. We provide
// the store to the route definitions so that routes have access to it for
// hooks such as `onEnter`.
const routes = makeRoutes(store);

const startApp = async () => {
    // in case browser does not support native Intl, polyfill it
    // see http://formatjs.io/guides/runtime-environments
    // TODO: possibly better to load only one language here?
    if (!window.Intl || !window.matchMedia) {
        window.Intl = await import(/* webpackChunkName: "intl" */ 'intl');
        await import(/* webpackChunkName: "match-media" */ 'match-media');

        if (locale && !window.Intl.PluralRules) {
            await Promise.all([
                import(
                    /* webpackChunkName: "intl-pluralrules/polyfill" */ '@formatjs/intl-pluralrules/polyfill'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/en.js" */ '@formatjs/intl-pluralrules/dist/locale-data/en.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/fr.js" */ '@formatjs/intl-pluralrules/dist/locale-data/fr.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/de.js" */ '@formatjs/intl-pluralrules/dist/locale-data/de.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/it.js" */ '@formatjs/intl-pluralrules/dist/locale-data/it.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/nl.js" */ '@formatjs/intl-pluralrules/dist/locale-data/nl.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/pl.js" */ '@formatjs/intl-pluralrules/dist/locale-data/pl.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/es.js" */ '@formatjs/intl-pluralrules/dist/locale-data/es.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-pluralrules/jsonp/cz.js" */ '@formatjs/intl-pluralrules/dist/locale-data/cs.js'
                ),
            ]);
        }
        if (!window.Intl.RelativeTimeFormat) {
            await Promise.all([
                import(
                    /* webpackChunkName: "intl-relativetimeformat/polyfill" */ '@formatjs/intl-relativetimeformat/polyfill'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/en.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/en.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/fr.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/fr.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/de.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/de.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/it.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/it.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/nl.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/nl.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/pl.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/pl.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/es.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/es.js'
                ),
                import(
                    /* webpackChunkName: "intl/intl-relativetimeformat/jsonp/cz.js" */ '@formatjs/intl-relativetimeformat/dist/locale-data/cs.js'
                ),
            ]);
        }
    }

    // Set the client device
    setClientDimensions(store);
    setClientOffset(store);
    setTabVisibility(store);

    window.addEventListener(
        'resize',
        throttle(() => setClientDimensions(store), 200),
    );
    window.addEventListener(
        'scroll',
        throttle(() => setClientOffset(store), 400),
    );
    document.addEventListener(getVisibilityChangeEventName(), () => setTabVisibility(store));

    const translations = await getTranslations(locale);
    // client side rendering will mount the app into #react-container
    // runtime errors in dev will show redbox overlay with stack trace
    const root = document.getElementById('react-container');
    if (__DEV__) {
        const RedBox = require('redbox-react');
        try {
            render(
                <AppRoot
                    store={store}
                    routes={routes}
                    translations={translations}
                    config={config}
                />,
                root,
            );
        } catch (e) {
            console.error(e);
            render(<RedBox error={e} />, root);
        }
    } else {
        render(
            <AppRoot store={store} routes={routes} translations={translations} config={config} />,
            root,
        );
    }
};

if (!Element.prototype.replaceWith) {
    Element.prototype.replaceWith = replaceWith;
}
if (!CharacterData.prototype.replaceWith) {
    CharacterData.prototype.replaceWith = replaceWith;
}
if (!DocumentType.prototype.replaceWith) {
    DocumentType.prototype.replaceWith = replaceWith;
}

startApp();
